import { useEffect, useRef, useCallback, createElement } from "react";
import { useNavigate } from "react-router-dom";

import { chatStoreInstance } from "@/components/VibeChat/hooks/useVibeChat";
import { ComputerAgentResults } from "@/components/VibeChat/stores/types";
import { StreamEventType } from "@/components/VibeChat/types";
import {
  DetectedResource,
  RESOURCE_LABELS,
  RESOURCE_ENTITY_TYPE,
  buildDedupKey,
  detectResources,
} from "@/components/VibeChat/utils/resourceDetection";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { ToastType } from "@/enums";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { displayToast } from "@/utils/toast";

export const useVibeChatNotifications = (isExpanded: boolean) => {
  const navigate = useNavigate();
  const workspace = useActiveWorkspace();
  const notifiedRef = useRef(new Set<string>());

  const showResourceToast = useCallback(
    (resource: DetectedResource) => {
      const label = RESOURCE_LABELS[resource.type];
      const entityType = RESOURCE_ENTITY_TYPE[resource.type];

      displayToast(`${label} created: ${resource.name}`, ToastType.success, {
        duration: Infinity,
        closeButton: true,
        icon: createElement(EntityIcon, { type: entityType, size: "sm" }),
        action: {
          label: "Open",
          onClick: () => navigate(resource.url),
        },
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (!workspace?.id) {
      return;
    }

    const workspaceId =
      typeof workspace.id === "string" ? parseInt(workspace.id, 10) : workspace.id;

    const unsubscribe = chatStoreInstance.addEventListener((eventType, data) => {
      if (isExpanded) {
        return;
      }

      if (eventType !== StreamEventType.COMPUTER_AGENT_COMPLETE) {
        return;
      }

      const results = data.results as ComputerAgentResults | undefined;
      if (!results) {
        return;
      }

      // Skip resource toasts when follow mode is active (user is auto-navigated)
      if (chatStoreInstance.isFollowMode) {
        return;
      }

      const resources = detectResources(results, workspaceId);

      resources.forEach(resource => {
        const key = buildDedupKey(resource);
        if (!notifiedRef.current.has(key)) {
          notifiedRef.current.add(key);
          showResourceToast(resource);
        }
      });
    });

    return unsubscribe;
  }, [isExpanded, workspace?.id, showResourceToast]);
};
